import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "w-full lg:grid lg:grid-cols-1 min-h-[100vh] pt-14 sm:pt-0" }
const _hoisted_2 = { class: "flex items-center justify-center py-12 sm:pt-[150px]" }
const _hoisted_3 = { class: "mx-auto grid w-full p-4 sm:p-0 sm:w-[450px] gap-10" }
const _hoisted_4 = { class: "grid gap-6" }
const _hoisted_5 = { class: "flex justify-center gap-3" }
const _hoisted_6 = ["onUpdate:modelValue", "onInput", "onKeydown", "onFocus", "disabled"]
const _hoisted_7 = { class: "grid gap-4 mt-4" }

import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast/use-toast'
import { Toaster } from '@/components/ui/toast'
import { ref, reactive, onMounted } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { useAuth } from '@/composables/useAuth'
import { useRouter } from 'vue-router'
import { useTenant } from '@/composables/useTenant'


export default /*@__PURE__*/_defineComponent({
  __name: 'TwoFactorView',
  setup(__props) {

const { toast } = useToast()
const { validate2AF, isAuthLoading } = useAuth()
const { isTradersPlatform } = useTenant()
const router = useRouter()

// Refs para os inputs individuais
const digitInputs = ref<(HTMLInputElement | null)[]>([])
const codeArray = reactive<string[]>(Array(6).fill(''))

const handleCodeInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  // Permitir apenas números
  if (!/^\d?$/.test(value)) {
    input.value = ''
    codeArray[index] = ''
    return
  }

  codeArray[index] = value

  // Mover para o próximo input se digitou um número
  if (value && index < 5) {
    setTimeout(() => {
      digitInputs.value[index + 1]?.focus()
    }, 10)
  }
}

const handleKeyDown = (index: number, event: KeyboardEvent) => {
  const key = event.key

  // Se pressionar backspace e o campo estiver vazio, voltar para o anterior
  if (key === 'Backspace' && !codeArray[index] && index > 0) {
    setTimeout(() => {
      digitInputs.value[index - 1]?.focus()
    }, 10)
  }

  // Se as setas forem pressionadas
  if (key === 'ArrowLeft' && index > 0) {
    digitInputs.value[index - 1]?.focus()
  }
  if (key === 'ArrowRight' && index < 5) {
    digitInputs.value[index + 1]?.focus()
  }

  // Se pressionar Enter e o código estiver completo
  if (key === 'Enter' && codeArray.join('').length === 6) {
    handleValidate()
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const pastedData = event.clipboardData?.getData('text') || ''
  const numbers = pastedData.replace(/\D/g, '').slice(0, 6)

  // Preencher os campos com os números colados
  for (let i = 0; i < 6; i++) {
    codeArray[i] = numbers[i] || ''
  }

  // Focar no último campo preenchido ou no último campo
  const lastFilledIndex = Math.min(numbers.length, 5)
  setTimeout(() => {
    digitInputs.value[lastFilledIndex]?.focus()
  }, 10)
}

const handleFocus = (index: number) => {
  setTimeout(() => {
    digitInputs.value[index]?.select()
  }, 10)
}

const handleValidate = () => {
  const fullCode = codeArray.join('')
  if (fullCode.length !== 6) return

  validate2AF(
    fullCode,
    () => {
      toast({
        title: 'Sucesso',
        description: 'Autenticação realizada com sucesso!',
      })

      if (isTradersPlatform) {
        router.push({ name: 'trader.home' })
      } else {
        router.push({ name: 'courses.my' })
      }
    },
    () => {
      toast({
        title: 'Erro na validação',
        description: 'Código 2FA inválido ou expirado.',
        variant: 'destructive'
      })
      // Limpa o código em caso de erro para tentar novamente
      for (let i = 0; i < 6; i++) codeArray[i] = ''
      digitInputs.value[0]?.focus()
    }
  )
}

onMounted(() => {
  // Auto-focus no primeiro input ao carregar
  setTimeout(() => {
    digitInputs.value[0]?.focus()
  }, 100)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _cache[3] || (_cache[3] = _createStaticVNode("<div class=\"grid gap-2 text-center\" data-v-614c82c0><div class=\"flex flex-col items-center gap-4 mx-auto mb-2\" data-v-614c82c0><img src=\"https://dashboard.hoopay.com.br/img/hoopay-logo.png\" alt=\"Logo Hoopay\" class=\"max-h-[50px] w-auto object-contain\" data-v-614c82c0><h1 class=\"text-3xl font-bold\" data-v-614c82c0> Verificação em Duas Etapas </h1></div><p class=\"text-muted-foreground\" data-v-614c82c0> Digite o código de 6 dígitos gerado pelo seu aplicativo de autenticação. </p></div>", 1)),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (digit, index) => {
                return _withDirectives(_createElementVNode("input", {
                  key: index,
                  ref_for: true,
                  ref: (el) => { digitInputs.value[index] = el as HTMLInputElement | null },
                  "onUpdate:modelValue": ($event: any) => ((codeArray[index]) = $event),
                  type: "text",
                  maxlength: "1",
                  inputmode: "numeric",
                  pattern: "[0-9]*",
                  onInput: ($event: any) => (handleCodeInput(index, $event)),
                  onKeydown: ($event: any) => (handleKeyDown(index, $event)),
                  onPaste: handlePaste,
                  onFocus: ($event: any) => (handleFocus(index)),
                  disabled: _unref(isAuthLoading),
                  class: "w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-background border-2 border-input rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                }, null, 40, _hoisted_6), [
                  [_vModelText, codeArray[index]]
                ])
              }), 64))
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_unref(Button), {
                type: "submit",
                class: "w-full py-6",
                disabled: _unref(isAuthLoading) || codeArray.join('').length !== 6,
                onClick: handleValidate
              }, {
                default: _withCtx(() => [
                  (_unref(isAuthLoading))
                    ? (_openBlock(), _createBlock(_unref(Loader2), {
                        key: 0,
                        class: "w-4 h-4 mr-2 animate-spin"
                      }))
                    : _createCommentVNode("", true),
                  _cache[1] || (_cache[1] = _createTextVNode(" Verificar Código "))
                ]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_unref(Button), {
                variant: "outline",
                class: "w-full py-6",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$router.push({ name: 'login' })))
              }, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode(" Voltar para o Login ")
                ])),
                _: 1
              })
            ])
          ])
        ])
      ])
    ]),
    _createVNode(_unref(Toaster))
  ], 64))
}
}

})